// Quote Builder — left panel, "PLANS" tab.
//
// Drop zone for plans/elevations/3D images, kind filter, and a scrollable
// gallery of design thumbnails. Selected designs are attached to the quote
// via `attachedDesigns[]`.
//
// Backend: `/api/portal/portal-designs` (PG-backed; migration 0016 added
// the `kind` column to design_files). The legacy `kos_designs_v2`
// localStorage cache is kept as a one-time migration source — on first
// mount we push any LS rows to PG, then clear the LS entry.

import { confirmDialog, iconMarkup } from "@tkos/shared-ui";
import { openLightbox, isImageUrl, type LightboxImage } from "../../shell/lightbox";
import { openPinEditor } from "./pin-editor";
import { api } from "@tkos/api-client";
import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import { escapeHtml } from "./formatters";

const LEGACY_DESIGNS_KEY = "kos_designs_v2";
const LEGACY_MIGRATED_KEY = "kos_designs_v2_migrated";

export type DesignKind = "All" | "3D Image" | "Plan" | "Elevation" | "Other";
export type StoredKind = Exclude<DesignKind, "All">;

type Visibility = "shared" | "hidden";
type PinsVisibility = "internal" | "shared";

export interface Design {
  id: string;
  name: string;
  ext: string;
  dataUrl: string;
  kind: StoredKind;
  uploadedAt: number;
  notes?: string;
  /** Per-file share gate. Only `shared` files appear on the customer PDF.
   *  Defaults set by kind on upload (3D Image / Other → shared; Plan /
   *  Elevation → hidden). Internal PDF ignores this and shows every file. */
  visibility: Visibility;
  /** Apprentice Kit pin-set visibility, separate from `visibility` above:
   *  that gates the IMAGE, this gates the numbered pins on it. Defaults to
   *  'internal' and, in v1, 'shared' shows customers nothing — the flag is
   *  wired ahead of the fitter/customer-notes work. */
  pinsVisibility: PinsVisibility;
  /** "wizard" = a customer's wizard room photo, merged in read-only (no
   *  delete / share toggle — it's not a real design_files row). */
  source?: "wizard" | "design";
}

interface ServerDesign {
  id: string;
  name: string;
  ext: string;
  kind: string;
  dataUrl: string;
  sizeBytes: number;
  notes: string;
  uploadedAt: number;
  visibility: Visibility;
  pinsVisibility?: string;
  source?: string;
}

function defaultVisibilityForKind(kind: StoredKind): Visibility {
  // Plans + Elevations are private by default — they contain proprietary
  // layout information the retailer should opt-in to share. 3D renders and
  // miscellaneous attachments (inspiration, mood boards) default to shared.
  return kind === "Plan" || kind === "Elevation" ? "hidden" : "shared";
}

function coerceVisibility(v: unknown): Visibility {
  return v === "shared" ? "shared" : "hidden";
}

function coerceKind(v: unknown): StoredKind {
  const s = String(v ?? 'Other');
  if (s === "Plan" || s === "Elevation" || s === "3D Image") return s;
  return "Other";
}

function guessKindFromName(name: string): StoredKind {
  const n = name.toLowerCase();
  if (/3d|render|persp/.test(n)) return "3D Image";
  if (/elev/.test(n)) return "Elevation";
  if (/plan|floor|gp/.test(n)) return "Plan";
  return "Other";
}

/**
 * EXPORTED for the floating plan thumbnail (floating-plan.ts), which needs the
 * same customer-scoped list this panel shows. Deliberately shared rather than
 * reimplemented: the customerId guard below is a real one, and a second copy
 * of this call is a second chance to forget it.
 */
export async function fetchDesigns(customerId: string): Promise<Design[]> {
  // Customer-scoped: no customer assigned → nothing to show. (Passing an empty
  // customerId would make the server return the whole retailer gallery.)
  if (!customerId) return [];
  try {
    const res = await api<{ ok: boolean; designs: ServerDesign[] }>(
      `/portal/portal-designs?customerId=${encodeURIComponent(customerId)}`,
    );
    return (res.designs ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      ext: d.ext,
      dataUrl: d.dataUrl,
      kind: coerceKind(d.kind),
      uploadedAt: d.uploadedAt,
      notes: d.notes || "",
      visibility: coerceVisibility(d.visibility),
      pinsVisibility: d.pinsVisibility === "shared" ? "shared" : "internal",
      source: d.source === "wizard" ? "wizard" : "design",
    }));
  } catch (err) {
    console.warn("[qb-plans] fetch designs failed", err);
    return [];
  }
}

/** Flip a design's visibility on the server. Returns the new value on
 *  success, the original on failure. */
async function setDesignVisibility(id: string, next: Visibility): Promise<Visibility | null> {
  try {
    const res = await api<{ ok: boolean; design: ServerDesign }>(`/portal/portal-designs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ visibility: next }),
      headers: { "content-type": "application/json" },
    });
    return res.design ? coerceVisibility(res.design.visibility) : null;
  } catch (err) {
    console.warn("[qb-plans] visibility patch failed", err);
    return null;
  }
}

/** Re-categorise a design on the server. Returns the new kind on success,
 *  null on failure. */
async function setDesignKind(id: string, next: StoredKind): Promise<StoredKind | null> {
  try {
    const res = await api<{ ok: boolean; design: ServerDesign }>(`/portal/portal-designs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ kind: next }),
      headers: { "content-type": "application/json" },
    });
    return res.design ? coerceKind(res.design.kind) : null;
  } catch (err) {
    console.warn("[qb-plans] kind patch failed", err);
    return null;
  }
}

async function uploadDesign(input: {
  name: string;
  ext: string;
  kind: StoredKind;
  dataUrl: string;
  customerId?: string;
}): Promise<Design | null> {
  try {
    const payload = { ...input, visibility: defaultVisibilityForKind(input.kind) };
    const res = await api<{ ok: boolean; design: ServerDesign }>(`/portal/portal-designs`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "content-type": "application/json" },
    });
    if (!res.design) return null;
    return {
      id: res.design.id,
      name: res.design.name,
      ext: res.design.ext,
      dataUrl: res.design.dataUrl,
      // A fresh upload has no pins yet; the server defaults the column to
      // 'internal' and echoes it back, so trust that rather than assuming.
      pinsVisibility: res.design.pinsVisibility === "shared" ? "shared" : "internal",
      kind: coerceKind(res.design.kind),
      uploadedAt: res.design.uploadedAt,
      notes: res.design.notes,
      visibility: coerceVisibility(res.design.visibility),
      source: "design",
    };
  } catch (err) {
    console.warn("[qb-plans] upload failed", err);
    return null;
  }
}

async function deleteDesign(id: string): Promise<boolean> {
  try {
    await api<{ ok: boolean }>(`/portal/portal-designs/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    return true;
  } catch (err) {
    console.warn("[qb-plans] delete failed", err);
    return false;
  }
}

/** One-shot: push anything in the legacy `kos_designs_v2` LS into PG,
 *  then mark migrated so we don't try again. Safe to no-op on retry. */
async function migrateLegacyDesigns(): Promise<void> {
  try {
    if (localStorage.getItem(LEGACY_MIGRATED_KEY) === "1") return;
    const raw = localStorage.getItem(LEGACY_DESIGNS_KEY);
    if (!raw) {
      localStorage.setItem(LEGACY_MIGRATED_KEY, "1");
      return;
    }
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [];
    for (const d of list) {
      if (!d || typeof d !== "object") continue;
      if (!d.dataUrl || !d.name) continue;
      await uploadDesign({
        name: String(d.name),
        ext: String(d.ext || "").toLowerCase() || "png",
        kind: coerceKind(d.kind),
        dataUrl: String(d.dataUrl),
      });
    }
    localStorage.setItem(LEGACY_MIGRATED_KEY, "1");
    // Don't clear LEGACY_DESIGNS_KEY — leaving it intact means a user
    // who hops back to the legacy portal still sees their files.
  } catch (err) {
    console.warn("[qb-plans] legacy migration failed", err);
  }
}

export function mountPlansPanel(
  host: HTMLElement,
  store: QuoteStore,
  ctx: PageContext,
): () => void {
  let kindFilter: DesignKind = "All";
  let designs: Design[] = [];
  let destroyed = false;
  // Track the customer the gallery is currently scoped to, so the store
  // subscription only re-fetches when the assigned customer actually changes.
  let loadedCustomerId = " "; // sentinel: "never loaded"

  async function refresh(): Promise<void> {
    const cid = store.get().customerId ?? "";
    loadedCustomerId = cid;
    const next = await fetchDesigns(cid);
    if (destroyed) return;
    // Guard against a race where the customer changed mid-fetch.
    if ((store.get().customerId ?? "") !== cid) return;
    designs = next;
    render();
  }

  function render(): void {
    const q = store.get();
    const hasCustomer = !!(q.customerId ?? "");
    const all = designs;
    const visible = all.filter((d) => kindFilter === "All" || d.kind === kindFilter);

    // Build a single design card. Extracted so the gallery can render designs
    // grouped under their category heading rather than one flat list.
    const cardHtml = (d: Design): string => {
      const attached = q.attachedDesigns.includes(d.id);
      const shared = d.visibility === "shared";
      const readOnly = d.source === "wizard";
      const kindControl = readOnly
        ? `<div class="qb-plan-card__kind">Wizard photo</div>`
        : `<select class="qb-plan-card__kind-select" data-kind-for="${escapeHtml(d.id)}" title="Change category">
             ${(["Plan", "Elevation", "3D Image", "Other"] as StoredKind[])
               .map((k) => `<option value="${escapeHtml(k)}"${k === d.kind ? " selected" : ""}>${escapeHtml(k)}</option>`)
               .join("")}
           </select>`;
      return `
        <div class="qb-plan-card ${attached ? "is-attached" : ""}" data-design="${escapeHtml(d.id)}">
          <div class="qb-plan-card__thumb">${
            d.dataUrl
              ? `<img src="${escapeHtml(d.dataUrl)}" alt="${escapeHtml(d.name)}" />`
              : `<div class="qb-plan-card__placeholder">${escapeHtml(d.ext.toUpperCase() || "?")}</div>`
          }</div>
          <div class="qb-plan-card__meta">
            <div class="qb-plan-card__name" title="${escapeHtml(d.name)}">${escapeHtml(d.name)}</div>
            ${kindControl}
          </div>
          <div class="qb-plan-card__actions">
            ${readOnly
              ? ""
              : `<button
              type="button"
              class="qb-plan-card__share qb-plan-card__share--${shared ? "shared" : "hidden"}"
              data-share="${escapeHtml(d.id)}"
              title="${shared ? "Visible on the customer PDF — click to hide" : "Hidden from the customer PDF — click to share"}"
              aria-pressed="${shared ? "true" : "false"}"
            >${shared ? "Shared" : "Hidden"}</button>`}
            ${readOnly || !(d.dataUrl && isImageUrl(d.dataUrl))
              ? ""
              : `<button type="button" class="qb-plan-card__pin${d.pinsVisibility === "shared" ? " is-shared" : ""}"
                   data-pin="${escapeHtml(d.id)}"
                   title="Pin quote lines onto this drawing (Apprentice Kit — internal)">Pin</button>`}
            <button type="button" class="qb-plan-card__toggle" data-toggle="${escapeHtml(d.id)}">${attached ? "Attached ✓" : "Attach"}</button>
            ${readOnly
              ? ""
              : `<button type="button" class="qb-plan-card__del" data-del="${escapeHtml(d.id)}" title="Delete">×</button>`}
          </div>
        </div>
      `;
    };

    // Render the visible designs grouped under labelled sections so plans,
    // elevations and 3D images land in the right place instead of one lump.
    // (Read-only wizard photos fold into the 'Other' section.)
    const SECTIONS: ReadonlyArray<{ kind: StoredKind; label: string }> = [
      { kind: "Plan", label: "Plans" },
      { kind: "Elevation", label: "Elevations" },
      { kind: "3D Image", label: "3D Images" },
      { kind: "Other", label: "Other" },
    ];
    const galleryHtml = visible.length === 0
      ? `<div class="qb-plans__empty">No plans or designs for this customer yet — drop files above.</div>`
      : SECTIONS.map((sec) => {
          const group = visible.filter((d) => d.kind === sec.kind);
          if (group.length === 0) return "";
          return `
            <div class="qb-plans__section">
              <div class="qb-plans__section-head">
                <span>${escapeHtml(sec.label)}</span>
                <span class="qb-plans__section-count">${group.length}</span>
              </div>
              <div class="qb-plans__gallery">${group.map(cardHtml).join("")}</div>
            </div>`;
        }).join("");

    host.innerHTML = `
      <div class="qb-plans">
        <div class="qb-plans__head">
          <h3>Plans &amp; designs</h3>
          <span class="qb-plans__count">${hasCustomer ? `${visible.length} of ${all.length}` : ""}</span>
        </div>
        ${!hasCustomer
          ? `<div class="qb-plans__empty qb-plans__empty--no-customer">Assign a customer to see their plans and designs</div>`
          : `
        <div class="qb-plans__filters" role="tablist">
          ${(["All", "3D Image", "Plan", "Elevation", "Other"] as DesignKind[])
            .map((k) => `<button type="button" class="qb-chip ${k === kindFilter ? "is-active" : ""}" data-kind="${escapeHtml(k)}">${escapeHtml(k)}</button>`)
            .join("")}
        </div>
        <div class="qb-plans__drop" data-role="drop" tabindex="0">
          ${iconMarkup("upload", { size: "lg" })}
          <div>Drop plans, elevations or 3D renders here</div>
          <div class="qb-plans__hint">or click to browse</div>
          <input type="file" multiple accept="image/*,.pdf" data-role="file" hidden />
        </div>
        ${galleryHtml}`}
      </div>
    `;

    // ── Filter chips
    host.querySelectorAll<HTMLButtonElement>(".qb-chip[data-kind]").forEach((btn) => {
      btn.addEventListener("click", () => {
        kindFilter = btn.dataset.kind as DesignKind;
        render();
      });
    });

    // ── Image thumbs → in-portal lightbox (prev/next cycles the visible image
    // designs). Share is auto-hidden for the data: URL plans.
    const planImgs = visible.filter((d) => d.dataUrl && isImageUrl(d.dataUrl));
    if (planImgs.length) {
      const lb: LightboxImage[] = planImgs.map((d) => ({ url: d.dataUrl as string, name: d.name }));
      host.querySelectorAll<HTMLElement>(".qb-plan-card").forEach((card) => {
        const idx = planImgs.findIndex((d) => d.id === card.dataset.design);
        if (idx < 0) return;
        const thumb = card.querySelector<HTMLElement>(".qb-plan-card__thumb");
        if (!thumb) return;
        thumb.style.cursor = "zoom-in";
        thumb.addEventListener("click", () => openLightbox({ images: lb, startIndex: idx }));
      });
    }

    // ── Pin annotation (Apprentice Kit). Internal surface; the editor is a
    //    retailer-only overlay rather than the shared lightbox, which is also
    //    rendered by the customer and driver apps.
    host.querySelectorAll<HTMLButtonElement>("[data-pin]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const id = btn.dataset["pin"] ?? "";
        const design = all.find((d) => d.id === id);
        if (!design || !design.dataUrl) return;
        openPinEditor({
          designFileId: design.id,
          imageUrl: design.dataUrl,
          imageName: design.name,
          pinsVisibility: design.pinsVisibility,
          store,
          showToast: (m, k) => ctx.showToast(m, k ?? "success"),
          onVisibilityChange: (v) => {
            // Keep the panel's copy in step so reopening reflects the change
            // without a refetch.
            design.pinsVisibility = v;
            render();
          },
        });
      });
    });

    // ── Drop zone
    const drop = host.querySelector<HTMLElement>("[data-role='drop']");
    const fileInp = host.querySelector<HTMLInputElement>("[data-role='file']");
    if (drop && fileInp) {
      drop.addEventListener("click", () => fileInp.click());
      drop.addEventListener("dragover", (e) => {
        e.preventDefault();
        drop.classList.add("is-dragging");
      });
      drop.addEventListener("dragleave", () => drop.classList.remove("is-dragging"));
      drop.addEventListener("drop", (e) => {
        e.preventDefault();
        drop.classList.remove("is-dragging");
        const files = e.dataTransfer?.files;
        if (files) void importFiles(files);
      });
      fileInp.addEventListener("change", () => {
        if (fileInp.files) void importFiles(fileInp.files);
        fileInp.value = "";
      });
    }

    // ── Share toggle (Shared / Hidden badge)
    host.querySelectorAll<HTMLButtonElement>("[data-share]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = btn.dataset.share ?? "";
        const target = designs.find((x) => x.id === id);
        if (!target) return;
        const next: Visibility = target.visibility === "shared" ? "hidden" : "shared";
        const saved = await setDesignVisibility(id, next);
        if (destroyed) return;
        if (!saved) {
          ctx.showToast("Couldn't update share status — try again", "error");
          return;
        }
        target.visibility = saved;
        render();
        ctx.showToast(saved === "shared"
          ? "Design will appear on the customer PDF"
          : "Design hidden from the customer PDF",
          "info",
        );
      });
    });

    // ── Re-categorise (kind select on each card → moves it to that section)
    host.querySelectorAll<HTMLSelectElement>("[data-kind-for]").forEach((sel) => {
      sel.addEventListener("change", async () => {
        const id = sel.dataset.kindFor ?? "";
        const target = designs.find((x) => x.id === id);
        if (!target) return;
        const next = coerceKind(sel.value);
        if (next === target.kind) return;
        const saved = await setDesignKind(id, next);
        if (destroyed) return;
        if (!saved) {
          ctx.showToast("Couldn't change category — try again", "error");
          render();
          return;
        }
        target.kind = saved;
        render();
        ctx.showToast(`Moved to ${saved}`, "info");
      });
    });

    // ── Attach toggles
    host.querySelectorAll<HTMLButtonElement>("[data-toggle]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.toggle ?? "";
        store.set((d) => {
          const i = d.attachedDesigns.indexOf(id);
          if (i >= 0) d.attachedDesigns.splice(i, 1);
          else d.attachedDesigns.push(id);
        });
        render();
      });
    });

    // ── Delete (× button on each card)
    host.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const id = btn.dataset.del ?? "";
        if (!id) return;
        if (!(await confirmDialog("Delete this design? This can't be undone.", {
          title: "Delete design",
          confirmLabel: "Delete",
          destructive: true,
        }))) return;
        const ok = await deleteDesign(id);
        if (destroyed) return;
        if (!ok) {
          ctx.showToast("Couldn't delete — try again", "error");
          return;
        }
        designs = designs.filter((x) => x.id !== id);
        // Also detach from the quote if it was attached.
        store.set((d) => {
          const i = d.attachedDesigns.indexOf(id);
          if (i >= 0) d.attachedDesigns.splice(i, 1);
        });
        render();
      });
    });
  }

  async function importFiles(files: FileList): Promise<void> {
    let added = 0;
    for (const f of Array.from(files)) {
      const ext = (f.name.split(".").pop() ?? "").toLowerCase();
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => resolve("");
        reader.readAsDataURL(f);
      });
      if (!dataUrl) continue;
      const uploaded = await uploadDesign({
        name: f.name,
        ext,
        kind: guessKindFromName(f.name),
        dataUrl,
        customerId: store.get().customerId ?? "",
      });
      if (uploaded) {
        designs.unshift(uploaded);
        added++;
      }
    }
    if (destroyed) return;
    if (added > 0) {
      ctx.showToast(`Imported ${added} file${added === 1 ? "" : "s"}`, "success");
    } else {
      ctx.showToast("Upload failed — try again", "error");
    }
    render();
  }

  // Initial paint with empty state while we fetch from server.
  render();
  void migrateLegacyDesigns().then(() => refresh());

  // Re-fetch when the assigned customer changes (assigned / swapped / cleared);
  // otherwise just re-render (e.g. attach-toggle changes elsewhere).
  const unsub = store.subscribe(() => {
    if ((store.get().customerId ?? "") !== loadedCustomerId) {
      void refresh();
    } else {
      render();
    }
  });
  return () => {
    destroyed = true;
    unsub();
  };
}
